import {captureOptions} from '../lib/offline/captureCache';
import React, {createContext, useContext, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {listBusinesses} from '../lib/businesses/businessService';
import {listCurrentAccountantAccess} from '../lib/accountants/accountantService';
import {supabase} from '../lib/supabase/client';

const ACTIVE_BUSINESS_KEY = 'silatrail.activeBusinessId';
const BusinessContext = createContext(null);

export function BusinessProvider({children}) {
  const queryClient = useQueryClient();
  const generation = useRef(0);
  const accountId = useRef(null);
  const [userId, setUserId] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [activeBusiness, setActiveBusinessState] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const loadBusinesses = useCallback(async (preferredId) => {
    const requestGeneration = ++generation.current;
    if (!supabase) {
      setBusinesses([]);
      setActiveBusinessState(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const {data: sessionData, error: userError} = await supabase.auth.getSession();
      const userData = {user: sessionData.session?.user};
      if (requestGeneration !== generation.current) return;

      if (userError) throw userError;

      if (!userData.user) {
        setUserId(null);
        setBusinesses([]);
        setActiveBusinessState(null);
        return;
      }

      const nextBusinesses = await captureOptions(userData.user.id, 'account', 'businesses', () => listBusinesses(userData.user.id));
      let accountantAccess=[];
      try { accountantAccess=await listCurrentAccountantAccess(userData.user.id); } catch { accountantAccess=[]; }
      if (requestGeneration !== generation.current) return;
      const accountantMap=new Map(accountantAccess.map(item=>[item.business_id,item]));
      const accessibleBusinesses=nextBusinesses.map(business=>{
        const external=accountantMap.get(business.id);
        if (business.created_by===userData.user.id) return {...business,_accessMode:'owner',_permissionLevel:'owner'};
        if (external) return {...business,_accessMode:'accountant',_permissionLevel:external.permission_level,_invitationId:external.invitation_id};
        return {...business,_accessMode:'member',_permissionLevel:'member'};
      });
      accountId.current = userData.user.id;
      setUserId(userData.user.id);
      const storedId = preferredId || window.localStorage.getItem(`${ACTIVE_BUSINESS_KEY}:${userData.user.id}`);
      const storedBusiness = accessibleBusinesses.find((item) => item.id === storedId);
      const nextActiveBusiness = storedBusiness || accessibleBusinesses[0] || null;

      setBusinesses(accessibleBusinesses);
      setActiveBusinessState(nextActiveBusiness);

      if (nextActiveBusiness) {
        window.localStorage.setItem(`${ACTIVE_BUSINESS_KEY}:${userData.user.id}`, nextActiveBusiness.id);
      } else {
        window.localStorage.removeItem(`${ACTIVE_BUSINESS_KEY}:${userData.user.id}`);
      }
    } catch (loadError) {
      if (requestGeneration !== generation.current) return;
      setError(loadError.message || 'Unable to load your businesses.');
      setBusinesses([]);
      setActiveBusinessState(null);
    } finally {
      if (requestGeneration === generation.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBusinesses();
    const online = () => loadBusinesses();
    window.addEventListener('online', online);

    if (!supabase) return () => window.removeEventListener('online', online);

    let timer;
    const {data} = supabase.auth.onAuthStateChange((_event, session) => {
      const nextId = session?.user?.id || null;
      if (nextId !== accountId.current) {
        ++generation.current;
        accountId.current = nextId;
        setIsLoading(true);
        setUserId(nextId);
        setBusinesses([]);
        setActiveBusinessState(null);
        queryClient.clear();
        
      }
      // Leave the auth callback before calling another Supabase auth method.
      window.clearTimeout(timer);
      timer = window.setTimeout(() => loadBusinesses(), 0);
    });

    return () => {
      ++generation.current;
      window.removeEventListener('online', online);
      window.clearTimeout(timer);
      data.subscription.unsubscribe();
    };
  }, [loadBusinesses, queryClient]);

  const switchBusiness = (businessId) => {
    const nextBusiness = businesses.find((business) => business.id === businessId);

    if (!nextBusiness || !nextBusiness.is_active) {
      setError('That Business is unavailable. The first active Business was selected.');
      const fallback = businesses.find((business) => business.is_active) || null;
      setActiveBusinessState(fallback);

      if (fallback) {
        window.localStorage.setItem(`${ACTIVE_BUSINESS_KEY}:${userId}`, fallback.id);
      }

      return;
    }

    setError('');
    setActiveBusinessState(nextBusiness);
    window.localStorage.setItem(`${ACTIVE_BUSINESS_KEY}:${userId}`, nextBusiness.id);
  };

  const setActiveBusiness = (business) => {
    if (!business) {
      setActiveBusinessState(null);
      if (userId) window.localStorage.removeItem(`${ACTIVE_BUSINESS_KEY}:${userId}`);
      return;
    }

    switchBusiness(business.id);
  };

  const value = useMemo(() => ({
    userId,
    activeBusiness,
    activeAccess:activeBusiness ? {
      mode:activeBusiness._accessMode || (activeBusiness.created_by===userId?'owner':'member'),
      permissionLevel:activeBusiness._permissionLevel || 'member',
      readOnly:activeBusiness._accessMode==='accountant'
    } : null,
    businesses,
    setActiveBusiness,
    switchBusiness,
    isLoading,
    error,
    reloadBusinesses: loadBusinesses
  }), [userId, activeBusiness, businesses, isLoading, error]);

  return (
    <BusinessContext.Provider value={value}>
      {children}
    </BusinessContext.Provider>
  );
}

export function useBusiness() {
  const context = useContext(BusinessContext);

  if (!context) {
    throw new Error('useBusiness must be used inside BusinessProvider.');
  }

  return context;
}
